import React, { useState, useEffect } from "react";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { OwcIcon, OwcHyperlink, OwcButton } from "@one/react";
import styles from "./Banner.module.scss";
import { withApollo } from "react-apollo";
import { LIST_NOTIFICATION_BANNER } from "../gql/bookingapi/queries";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { NOTIFICATION_TYPES, BANNER_COLORS, DURATION } from "../constants";

const DEFAULT_COLOR_SCHEME = {
  Heading: "var(--one-color-cobas-blue-600)",
  TextToDisplay: "var(--one-color-legacy-blue-200)",
  Icon: "circle_info",
  IconType: "outlined"
};

/**
 * NavigationArrow Component
 *
 * A reusable button component for navigating between banners or slides.
 *
@param {string} direction - The direction of the arrow ('prev' or 'next').
@param {boolean} isDisabled - Indicates if the button is disabled.
@param {function} onClick - The function to call when the button is clicked.
@param {string} className - Additional CSS class for styling
@returns {JSX.Element} The rendered NavigationArrow component.
*/
const NavigationArrow = React.memo(({
  direction,
  isDisabled,
  onClick,
  className
}) => {
  const ArrowIcon = direction === 'prev' ? ArrowBackIosNewIcon : ArrowForwardIosIcon;

  const style = {
    display: 'flex',
    alignItems: 'center',
    margin: direction === 'prev' ? '0 8px 0 16px' : '0 16px 0 8px',
    cursor: isDisabled ? 'default' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    border: 'none',
    background: 'none',
    color: '#000000'
  };

  return (
    <OwcButton
      className={className}
      flat
      style={style}
      onClick={onClick}
      disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
      aria-label={`${direction === 'prev' ? 'Previous' : 'Next'} banner`}
      aria-disabled={isDisabled}
    >
      <ArrowIcon fontSize="small" />
    </OwcButton>
  );
});


/** Banner Component
 *
 * Displays notification banners with navigation controls.
 *
 * @param {object} client - Apollo client for GraphQL queries.
 * @returns {JSX.Element} The rendered Banner component.
 */
const Banner = ({ client }) => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const loadBannerLists = async () => {
      try {
        const { data: { listNotificationActiveBookingBanners } } = await client.query({
          query: LIST_NOTIFICATION_BANNER,
          fetchPolicy: "network-only",
          variables: { nextToken: null },
        });
        const banners = listNotificationActiveBookingBanners || [];
        banners.sort((a, b) => b.color.localeCompare(a.color));
        setBanners(banners);
        setCurrentIndex(0);
      } catch (error) {
        snackbarService.show({
          type: "error",
          message: 'Server Error: Unable to fetch notification banners',
          duration: DURATION
        })
        setBanners([]);
      }
    };
    loadBannerLists();
  }, [client]);

  const handleNext = () => {
    setCurrentIndex(prevIndex =>
      prevIndex < banners.length - 1 ? prevIndex + 1 : prevIndex
    );
  };

  const handlePrev = () => {
    setCurrentIndex(prevIndex =>
      prevIndex > 0 ? prevIndex - 1 : prevIndex
    );
  };

  const currentBanner = banners[currentIndex];
  const totalBanners = banners.length;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === banners.length - 1;

  let colorScheme = DEFAULT_COLOR_SCHEME;
  if (currentBanner) {
    const colorKey = Object.keys(NOTIFICATION_TYPES).find(
      key => NOTIFICATION_TYPES[key] === currentBanner.color
    );
    colorScheme = BANNER_COLORS[colorKey] || DEFAULT_COLOR_SCHEME;
  }

  const containerStyle = {
    borderLeft: `2px solid ${colorScheme.Heading}`,
    borderRight: `2px solid ${colorScheme.Heading}`,
    backgroundColor: colorScheme.TextToDisplay,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: (totalBanners > 1) ? 'space-between' : 'center',
    margin: '0 1rem',
    paddingLeft: '0',
    height: '3rem',
  };


  const iconLinkStyle = {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    cursor: 'pointer'
  };

  const iconStyle = {
    color: colorScheme.Heading,
    fontSize: 30,
    paddingRight: 15
  };

  const hyperlinkIconStyle = {
    color: colorScheme.Heading,
    fontSize: 30,
    paddingRight: 15,
    cursor: 'pointer'
  };

  if (totalBanners === 0) {
    return null;
  }

  return (
    <div
      style={containerStyle}
      role="banner"
      aria-live="polite"
      aria-label={`Banner ${currentIndex + 1} of ${totalBanners}`}
    >


      {totalBanners > 1 && (
        <NavigationArrow
          direction="prev"
          isDisabled={isFirst}
          onClick={handlePrev}
          className={styles.BannerArrow}
        />
      )}
      <div className={styles.BannerContent}>
        {currentBanner.infoURL ?
          <OwcHyperlink
            href={currentBanner.infoURL}
            target="_blank"
            className={styles.IconLink}
            style={iconLinkStyle}
            aria-label="Read more information"
          >
            <OwcIcon
              name={colorScheme.Icon}
              type={colorScheme.IconType}
              style={hyperlinkIconStyle}
            />
          </OwcHyperlink>
          :
          <OwcIcon
            name={colorScheme.Icon}
            type={colorScheme.IconType}
            style={iconStyle}
          />
        }


        <span className={styles.BannerHeading}>
          <strong>{currentBanner.heading}</strong>
        </span>

        <span className={styles.BannerText}>
          {currentBanner.displayText}
        </span>
        {currentBanner.infoURL && (
          <OwcHyperlink
            className={styles.BannerLink}
            href={currentBanner?.infoURL}
            target="_blank"
            aria-label="Read more details"
          >
            <span style={{ fontSize: "12px" }}>Read More</span>
          </OwcHyperlink>
        )}
      </div>
      {totalBanners > 1 && (
        <NavigationArrow
          direction="next"
          isDisabled={isLast}
          onClick={handleNext}
          className={styles.BannerArrow}
        />
      )}
    </div>
  );
};


export default withApollo(Banner);
